import api from "../config/api";

// Ships this app's warnings/errors to the admin log viewer (Admin -> Activity Logs).
// Entries are sent one per log (grouped into issues on the server), crashes and unhandled
// promise rejections are captured globally, and a page can't flood the log.
const SOURCE = "user_webapp";
const LOG_PATH = "/admin/activity-logs";
const MAX_PER_MINUTE = 30;

const logQueue = [];
let flushTimer = null;
let sentThisMinute = 0;
let minuteStartedAt = Date.now();
const recentlySeen = new Map();

const post = (body) => api.post(LOG_PATH, body);

const flushLogs = async () => {
  if (logQueue.length === 0) return;
  const entries = logQueue.splice(0, logQueue.length);
  try {
    await post({ source: SOURCE, entries });
  } catch (err) {
    // Logging failures must not break the app.
    if (process.env.NODE_ENV !== "production") console.error("[ActivityLog] Failed to ship logs:", err);
  }
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushLogs();
  }, 5000);
};

// Same message within 30s is sent once; at most MAX_PER_MINUTE entries per minute.
const shouldSend = (key) => {
  const now = Date.now();
  if (now - minuteStartedAt > 60000) {
    minuteStartedAt = now;
    sentThisMinute = 0;
  }
  if (sentThisMinute >= MAX_PER_MINUTE) return false;
  const last = recentlySeen.get(key);
  if (last && now - last < 30000) return false;
  recentlySeen.set(key, now);
  if (recentlySeen.size > 200) recentlySeen.clear();
  sentThisMinute += 1;
  return true;
};

export const logActivity = (level, category, event, message, metadata = {}) => {
  if (!shouldSend(`${level}|${event}|${message}`)) return;
  const entry = {
    source: SOURCE,
    level,
    category,
    event,
    message: String(message || "").slice(0, 2000),
    metadata: {
      ...metadata,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    },
  };
  if (level === "fatal") {
    post({ source: SOURCE, entries: [entry] }).catch(() => {});
  } else {
    logQueue.push(entry);
    scheduleFlush();
  }
};

export const logApiError = (error) => {
  const url = error?.config?.url || "";
  if (url.includes("activity-logs")) return; // never log the logger's own failures
  const status = error?.response?.status;
  logActivity(
    status >= 500 || !status ? "error" : "warn",
    "api",
    "API_ERROR",
    `${(error?.config?.method || "").toUpperCase()} ${url.split("?")[0]} -> ${status || "network error"}: ${error?.response?.data?.message || error?.message || "request failed"}`,
    {
      status,
      url,
      method: error?.config?.method,
      responseData: error?.response?.data,
    }
  );
};

export const flushActivityLogs = flushLogs;

// Crashes outside React rendering (event handlers, timers) and unhandled promise rejections.
window.addEventListener("error", (event) => {
  logActivity("error", "ui", "JS_ERROR", event.message || "Script error", {
    stack: event.error?.stack,
    file: event.filename,
    line: event.lineno,
    column: event.colno,
  });
});
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  if (reason?.isAxiosError) return; // already logged by the API client
  logActivity("error", "ui", "UNHANDLED_REJECTION", reason?.message || String(reason), { stack: reason?.stack });
});
window.addEventListener("pagehide", flushLogs);
